import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { CityEscapeFlowChart } from "../../components/CityEscapeFlowChart/CityEscapeFlowChart";
import { CityEscapeMatchesList } from "../../components/CityEscapeMatchesList.component";
import { CityEscapesDropdown } from "../../components/CityEscapesDropdown.component";
import FilterBar from "../../components/FilterBar/FilterBar.component";
import { LoadingGuard } from "../../components/LoadingGuard";
import { ValueDisplayCard } from "../../components/NumericMetricCard.component";
import { CardMediaList } from "../../components/PropMediaList.component";
import { DetailedCityEscape, DetailedProps } from "../../directus/collections";
import { useTotalWithdrawalsEvent } from "../../hooks";
import {
  useCityEscapesDatasource,
  useFullCityEscapeData,
} from "../../hooks/cityEscapes.hooks";
import {
  usePropAvailability,
  useTotalPropDeliveries,
  useTotalPropWithdrawals,
} from "../../hooks/props.hooks";
import { useFilters } from "../../providers/FiltersProvider";
import { useSelection } from "../../providers/SelectionProvider";
import { LangSwitchOption } from "../../types/misc";
import { getActiveTranslation } from "../../utils/cityEscape";
import { CityEscapeOverview } from "./CityEscapeOverview";

export interface CityEscapePageProps {
  onSceneSelection?: (sceneId: string) => void;
}

export function CityEscapePage({ onSceneSelection }: CityEscapePageProps) {
  const { data: cityEscapeData, isLoading } = useCityEscapesDatasource();
  const selection = useSelection();
  const filters = useFilters();
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();

  const [selectedLanguage, setSelectedLanguage] =
    useState<LangSwitchOption>("it-IT");

  const selectedCityEscape = useFullCityEscapeData(
    selection.active.cityEscapeId
  );

  if (isLoading || selectedCityEscape.isLoading) {
    return (
      <Paper>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="50vh"
          gap={2}
        >
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Caricamento City Escapes...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Box maxHeight={"100%"} overflow="auto">
      <Stack>
        <Box p={2} display="flex" gap={2}>
          <FormControl fullWidth>
            <CityEscapesDropdown
              cityEscapes={cityEscapeData || []}
              selectedId={selection.active.cityEscapeId || undefined}
              onChange={(cityEscape) => {
                selection.setActiveSelection({
                  cityEscapeId: cityEscape?.id ?? null,
                });
              }}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="demo-simple-select-label">Lingua</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedLanguage}
              label="Language"
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              <MenuItem value="it-IT">Italiano</MenuItem>
              <MenuItem value="en-US">English</MenuItem>
              <MenuItem value="both">Compara</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Stack>
      <FilterBar
        sticky={{ top: 0 }}
        filters={filters.active}
        onClearAll={filters.clear}
        onReset={filters.reset}
        onEdit={filters.update}
      />
      {(selectedCityEscape.data && (
        <Stack p={2} spacing={2}>
          <CityEscapeOverview
            cityEscapeId={selectedCityEscape.data.id}
            filters={filters.active}
          />
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="basic tabs example"
          >
            <Tab label="Flow" />
            {/* <Tab label="Preview" /> */}
            <Tab label="Props" />
            <Tab label="Partite" />
          </Tabs>
          <CustomTabPanel value={activeTab} index={0}>
            {selectedCityEscape.data.id ? (
              <CityEscapeFlowChart
                cityEscape={selectedCityEscape.data}
                filters={filters.active}
                lang={selectedLanguage}
                height="75vh"
                direction="DOWN"
                controls={true}
                minimap={true}
                defaultZoom={0.5}
                onSelection={(node) => {
                  if (node?.data?.scene) {
                    const scene = node.data.scene as any;
                    onSceneSelection?.(scene.id);
                  }
                }}
              />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="300px"
              >
                <Typography variant="body1" color="text.secondary">
                  Errore: City Escape non valida
                </Typography>
              </Box>
            )}

            <Box
              mt={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                Scenes:{" "}
                {(selectedCityEscape.data?.scenes?.length || 0) +
                  (selectedCityEscape.data?.prologue ? 1 : 0) +
                  (selectedCityEscape.data?.epilogue ? 1 : 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Steps:{" "}
                {(selectedCityEscape.data?.prologue?.steps?.length || 0) +
                  (selectedCityEscape.data?.scenes?.reduce(
                    (acc, scene) => acc + (scene.steps?.length || 0),
                    0
                  ) || 0) +
                  (selectedCityEscape.data?.epilogue?.steps?.length || 0)}
              </Typography>
            </Box>
          </CustomTabPanel>
          <CustomTabPanel value={activeTab} index={1}>
            <CityEscapeGameProps
              cityEscape={selectedCityEscape.data}
              activeLang={selectedLanguage}
            />
          </CustomTabPanel>
          <CustomTabPanel value={activeTab} index={2}>
            <CityEscapeMatchesList
              cityEscapeId={selectedCityEscape.data.id}
              filters={filters.active}
            />
          </CustomTabPanel>
        </Stack>
      )) || (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="50vh"
          gap={2}
        >
          <Typography variant="h6" color="text.secondary">
            Nessuna City Escape trovata
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Non ci sono City Escapes disponibili per visualizzare il flow chart.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default CityEscapePage;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Paper
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      sx={{ padding: 2, minHeight: "100%" }}
    >
      {value === index && <Box>{children}</Box>}
    </Paper>
  );
}

function CityEscapePreview({
  cityEscapeId,
  sceneId,
}: {
  cityEscapeId?: string;
  sceneId?: string;
}) {
  const url = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (cityEscapeId) {
      urlParams.append("ce", cityEscapeId);
    }
    if (sceneId) {
      urlParams.append("scene", sceneId);
    }

    return new URL(
      `https://play.garipalli.com/it/city-escape/preview?${urlParams.toString()}`
    );
  }, [cityEscapeId, sceneId]);

  return (
    <Stack
      justifyContent={"stretch"}
      alignItems="stretch"
      width="100%"
      height="100%"
    >
      <iframe
        style={{
          flex: 1,
          width: "100%",
          height: "max-content",
          minHeight: "80vh",
        }}
        credentialless="true"
        src={url.toString()}
      ></iframe>
    </Stack>
  );
}

function CityEscapeGameProps({
  cityEscape,
  activeLang,
}: {
  cityEscape: DetailedCityEscape;
  activeLang: LangSwitchOption;
}) {
  // const activeTranslation = getActiveTranslation(cityEscape, activeLang);

  const getTranslation = useCallback(
    (prop: DetailedProps) => {
      return getActiveTranslation(prop, activeLang);
    },
    [cityEscape, activeLang]
  );

  return (
    <Stack spacing={1}>
      {cityEscape &&
        cityEscape.props.map((prop) => {
          const translation = getTranslation(prop);

          return (
            <Card key={prop.id} variant="outlined">
              <CardHeader
                title={translation?.name || `--`}
                subheader={`Id ${prop.id}`}
              />
              <CardMedia>
                <CardMediaList
                  media={prop.media}
                  imageListProps={{
                    sx: { width: "100%" },
                    cols: prop.media.length,
                    rowHeight: 200,
                  }}
                />
              </CardMedia>
              <CardContent>
                {prop.partners.map((partner) => {
                  return (
                    <Accordion key={partner.id}>
                      <AccordionSummary
                        id={`panel-header-${partner.id}`}
                        aria-controls={`panel-content-${partner.id}`}
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "stretch",
                        }}
                      >
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          flex={1}
                          spacing={2}
                        >
                          <Stack>
                            <Typography variant="h6">
                              {partner?.name || `Prop ${prop.id}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {partner.id}
                            </Typography>
                            <Typography variant="body1" mt={1}>
                              {partner?.description}
                            </Typography>
                          </Stack>
                          <Stack
                            direction={"row"}
                            justifyContent={"flex-end"}
                            flex={1}
                          >
                            <PropPartnerOverview
                              cityEscapeId={cityEscape.id}
                              prop={prop}
                            />
                          </Stack>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {partner?.description ||
                          `Description for Partner ${partner.id}`}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
    </Stack>
  );
}

function PropPartnerOverview({
  prop,
  cityEscapeId,
}: {
  prop: DetailedProps;
  cityEscapeId: string;
}) {
  return (
    <Paper sx={{ padding: 2, flex: 1 }} variant="outlined">
      <Stack spacing={2} direction="row" justifyContent={"flex-end"} flex={1}>
        <PropTotalDeliveriesValueCard propId={prop.id} />
        <PropTotalWithdrawalsValueCard
          cityEscapeIds={cityEscapeId}
          propId={prop.id}
        />
        <PropCurrentBalanceValueCard
          propId={prop.id}
          quantity={prop.quantity}
        />
        <PropsAvailabilityCard propId={prop.id} />
      </Stack>
    </Paper>
  );
}

function PropTotalDeliveriesValueCard({ propId }: { propId: string }) {
  const totalDeliveries = useTotalPropDeliveries({ propId });
  return (
    <ValueDisplayCard
      title="Spediti"
      value={totalDeliveries.data ?? 0}
      isLoading={totalDeliveries.isLoading}
      error={totalDeliveries.error}
    />
  );
}

function PropTotalWithdrawalsValueCard({
  cityEscapeIds,
  propId,
}: {
  cityEscapeIds: string | string[] | null;
  propId: string;
}) {
  const totalWithdrawals = useTotalPropWithdrawals({ propId });
  const totalWithdrawalsEvent = useTotalWithdrawalsEvent({ cityEscapeIds });
  return (
    <ValueDisplayCard
      title="Evasi"
      value={totalWithdrawals.data ?? 0}
      isLoading={totalWithdrawals.isLoading}
      error={totalWithdrawals.error}
      inf={
        <Tooltip title="Totale eventi evasione registrati">
          <LoadingGuard isLoading={totalWithdrawalsEvent.isLoading} size={10}>
            <span>{totalWithdrawalsEvent.data?.totalWithdrawals ?? 0}</span>
          </LoadingGuard>
        </Tooltip>
      }
    />
  );
}

function PropCurrentBalanceValueCard({
  propId,
  quantity,
}: {
  propId: string;
  quantity: number;
}) {
  return <ValueDisplayCard title="Saldo" value={quantity} />;
}

function PropsAvailabilityCard({ propId }: { propId: string }) {
  const availability = usePropAvailability(propId);
  return (
    <ValueDisplayCard
      title="Disponibilità"
      value={availability.data?.available ? "Disponibile" : "Non disponibile"}
      isLoading={availability.isLoading}
    />
  );
}

export function CityEscapeEditsHistory({
  cityEscapeId,
}: {
  cityEscapeId: string;
  propId: string;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Edit history</Typography>
      {/* <CityEscapeForm cityEscapeId={cityEscapeId} propId={propId} /> */}
    </Stack>
  );
}
